import React, { useMemo, useRef, useState } from "react";

import { Badge, Combobox, createListCollection, Field, Portal, Wrap } from "@metrea-ui/v2";

import { isTestEnv } from "src/utils/appUtils";

const getOptionLabel = (option) =>
	option?.label ??
	option?.name ??
	option?.display_name ??
	option?.username ??
	option?.email ??
	String(option?.id ?? "");

const normalizeLabelId = (typeName) =>
	`multi-select-${(typeName ?? "noname").replace(/\s+/g, "-").toLowerCase()}`;

const buildCollectionItems = (options = []) =>
	options.map((option) => ({
		label: getOptionLabel(option),
		value: String(option?.id ?? ""),
	}));

const FormMultiSelect = (props) => {
	const {
		type,
		items = [],
		selected = [],
		onUpdate,
		onChangeIds,
		required = false,
		helperText,
		error,
		isInvalid,
		errorText,
		hideItemId,
		hideItemRule,
		size,
		mode,
		disabled,
		closeOnSelect,
	} = props;

	const renderCount = useRef(0);
	renderCount.current += 1;

	const inputId = useMemo(() => normalizeLabelId(type?.name), [type?.name]);
	const [searchValue, setSearchValue] = useState("");
	const selectedIds = useMemo(
		() => (Array.isArray(selected) ? selected.map(String) : []),
		[selected]
	);

	const allOptions = useMemo(() => {
		const base = Array.isArray(items) ? items : [];
		const shouldHide = hideItemRule === undefined ? true : Boolean(hideItemRule);
		return base.filter((option) => (hideItemId && shouldHide ? option?.id !== hideItemId : true));
	}, [hideItemId, hideItemRule, items]);

	const filteredOptions = useMemo(() => {
		const query = (searchValue || "").toLowerCase();
		if (!query) return allOptions;
		return allOptions.filter((option) => getOptionLabel(option).toLowerCase().includes(query));
	}, [allOptions, searchValue]);

	const filteredCollectionItems = useMemo(
		() => buildCollectionItems(filteredOptions),
		[filteredOptions]
	);

	const optionMap = useMemo(() => {
		const map = new Map();
		allOptions.forEach((option) => map.set(String(option.id), option));
		return map;
	}, [allOptions]);

	const collectionItems = useMemo(() => {
		const itemsByValue = new Map();
		filteredCollectionItems.forEach((item) => itemsByValue.set(item.value, item));
		// Keep already-selected values in the backing collection even when the
		// current search text no longer matches them, otherwise the controlled
		// multi-select can stop suggesting the next typed option after one pick.
		selectedIds.forEach((id) => {
			if (itemsByValue.has(String(id))) return;
			const selectedOption = optionMap.get(String(id));
			if (!selectedOption) return;
			itemsByValue.set(String(id), {
				label: getOptionLabel(selectedOption),
				value: String(id),
			});
		});
		return [...itemsByValue.values()];
	}, [filteredCollectionItems, optionMap, selectedIds]);

	const collection = useMemo(
		// Keep already-selected values in the underlying collection so multi-select
		// typing still works after the first choice narrows the visible menu rows.
		() => createListCollection({ items: collectionItems }),
		[collectionItems]
	);

	const sortedSelectedIds = useMemo(
		() =>
			[...selectedIds].sort((leftId, rightId) => {
				const leftLabel = getOptionLabel(optionMap.get(String(leftId))).toLowerCase();
				const rightLabel = getOptionLabel(optionMap.get(String(rightId))).toLowerCase();
				return leftLabel.localeCompare(rightLabel);
			}),
		[optionMap, selectedIds]
	);

	const isDisabled =
		typeof disabled === "boolean"
			? disabled
			: String(mode || "")
					.trim()
					.toLowerCase()
					.startsWith("r");
	const invalid = Boolean(isInvalid ?? error);
	const helperContent = (typeof error === "string" && error.trim()) || errorText || helperText || "";
	const hasHelperContent = helperContent !== "";
	const helpId =
		(helperText || errorText || (typeof error === "string" && error.trim() !== "")) && inputId
			? `${inputId}-helptext`
			: undefined;

	const handleValueChange = (details) => {
		const nextIds = Array.isArray(details?.value) ? details.value : [];

		// Preserve the historical callback shape so existing Baseline/Scenario/User
		// handlers still receive the selected option objects after the direct-v2 migration.
		const selectedObjects = nextIds.map((id) => optionMap.get(String(id))).filter(Boolean);
		onUpdate?.(selectedObjects, type);
		onChangeIds?.(nextIds);
	};

	if (isTestEnv) {
		// Real-mode tests use this hook to exercise the same parent update path
		// without depending on the combobox popup implementation details.
		window.__RAVEN_TEST_HOOKS__ = window.__RAVEN_TEST_HOOKS__ || {};
		window.__RAVEN_TEST_HOOKS__.multiSelectUpdate = onUpdate;
	}

	if (process.env.NODE_ENV === "development" && props?.sx != null) {
		// eslint-disable-next-line no-console
		console.warn("[UI/common/FormMultiSelect] sx is not supported; use direct layout props instead", {
			sx: props.sx,
		});
	}

	return (
		<div style={{ marginTop: "10px", marginBottom: "10px", width: "100%" }}>
			<Field.Root required={required} invalid={invalid} style={{ width: "100%" }}>
				{type?.name ? (
					<Field.Label htmlFor={inputId} style={invalid ? { color: "#d32f2f" } : undefined}>
						{type.name}
						{required ? <Field.RequiredIndicator /> : null}
					</Field.Label>
				) : null}

				<div style={{ width: "100%" }}>
					<Combobox.Root
						data-testid={`select-${normalizeLabelId(type?.name).replace("multi-select-", "")}`}
						{...(isTestEnv ? { "data-test-hook": { onUpdate } } : {})}
						multiple
						closeOnSelect={closeOnSelect ?? false}
						value={selectedIds}
						collection={collection}
						onValueChange={handleValueChange}
						size={size === "small" ? "sm" : size === "medium" ? "md" : "sm"}
						disabled={isDisabled}
						width="100%"
					>
						<Wrap gap="2" mb="2">
							{sortedSelectedIds.map((id) => (
								<Badge key={id}>{getOptionLabel(optionMap.get(String(id)))}</Badge>
							))}
						</Wrap>

						<Combobox.Label srOnly>Select</Combobox.Label>

						<Combobox.Control
							style={{
								backgroundColor: isDisabled ? "#f5f5f5" : "white",
								border: invalid ? "1px solid #d32f2f" : "1px solid rgba(0,0,0,0.23)",
								borderRadius: 0,
								minHeight: 40,
							}}
						>
							<Combobox.Input
								id={inputId}
								placeholder={isDisabled ? "" : "Type to search..."}
								// Keep Raven's option filtering independent from the v2 suggestion state.
								onChange={(event) => setSearchValue(event.target.value)}
								style={{
									paddingTop: 8,
									paddingBottom: 8,
									paddingLeft: 12,
									paddingRight: 40,
									fontSize: "0.875rem",
								}}
							/>
							<Combobox.IndicatorGroup>
								<Combobox.ClearTrigger style={{ marginRight: 2 }} />
								<Combobox.Trigger />
							</Combobox.IndicatorGroup>
						</Combobox.Control>

						<Portal>
							<Combobox.Positioner>
								<Combobox.Content>
									<Combobox.ItemGroup>
										{filteredCollectionItems.map((item) => {
											return (
												<Combobox.Item key={item.value} item={item}>
													<Combobox.ItemText>{item.label}</Combobox.ItemText>
													<Combobox.ItemIndicator />
												</Combobox.Item>
											);
										})}

										{filteredCollectionItems.length === 0 ? (
											<Combobox.Empty>No matches</Combobox.Empty>
										) : null}
									</Combobox.ItemGroup>
								</Combobox.Content>
							</Combobox.Positioner>
						</Portal>
					</Combobox.Root>
				</div>

				<Field.HelperText id={helpId}>
					<span
						style={{
							display: "block",
							minHeight: "1.5em",
							fontSize: "0.75rem",
							color: invalid ? "#d32f2f" : "inherit",
							visibility: hasHelperContent ? "visible" : "hidden",
						}}
					>
						{hasHelperContent ? helperContent : "\u00A0"}
					</span>
				</Field.HelperText>
			</Field.Root>
		</div>
	);
};

export default FormMultiSelect;
